/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { omit } from 'lodash';
import { ResizableTable } from 'libs/utils/resizable-table';

// Components
import ListQuickEditor from 'shared/ui/components/editors/ListQuickEditor.vue';
import Paginator from 'shared/ui/components/Paginator.vue';

// Mixins
import baseSmartTable from 'shared/ui/mixins/table/base-smart-table';

const MAX_ITEMS_PER_PAGE = 10;
const RESIZABLE_HANDLE_WIDTH = 10;
const RESIZABLE_MIN_COLUMN_WIDTH = 120;
const RESIZABLE_CURSOR = 'col-resize';

const __vue_script__ = {
    name: 'SmartTable',

    components: { Paginator, ListQuickEditor },

    mixins: [baseSmartTable],

    props: {
        editor: {
            type: Object,
            default: null
        },

        event: {
            type: Object,
            default: null
        },

        itemsPerPage: {
            type: Number,
            default: MAX_ITEMS_PER_PAGE
        },

        totalItems: {
            type: Number,
            required: true
        },

        fullListProvided: {
            type: Boolean,
            default: false
        },

        hidePagination: {
            type: Boolean,
            default: false,
        },

        showPageNumbers: {
            type: Boolean,
            default: true,
        },

        autoRows: {
            type: Boolean,
            default: false
        },

        resizable: {
            type: Boolean,
            default: false
        },

        id: {
            type: String,
            default: null
        },

        columns: {
            type: Array,
            default: null
        },
    },

    data() {
        return {
            currentPage: 1,
            editorListeners: null,
            sidePanelContent: null,
            computedItemsPerPage: this.autoRows ? 0 : this.itemsPerPage,
            hasResizeCursor: false,
            columnWidths: [],
            columnBeingResized: null,
            resizableInstance: null,
        };
    },

    computed: {
        /** @returns {number} */
        maxPage() {
            return Math.ceil(this.totalItems / this.computedItemsPerPage || 1);
        },

        /** @returns {object} */
        editorAttrs() {
            return omit(this.$attrs, Object.keys(this.$options.propsData));
        },

        visibleItems() {
            if (!this.fullListProvided) {
                return this.items;
            } else {
                const offset = this.computedItemsPerPage * (this.currentPage - 1);
                return this.items.slice(offset, this.computedItemsPerPage * this.currentPage);
            }
        },

        hasActions() {
            return Boolean(this.$scopedSlots.actions?.());
        },

        computedClass() {
            return {
                'no-cursor': this.hasResizeCursor,
                'resizable': this.resizable,
            };
        },

        columnClasses() {
            if (this.columnBeingResized) {
                return {
                    [this.columnBeingResized]: ['column-dragging']
                };
            }
            return {};
        },
    },

    watch: {
        totalItems() {
            this.calculateMaxRows();
        },
        columns(newValue) {
            if (this.resizableInstance) {
                this.resizableInstance.columnsUpdated(newValue.map(column => column.field));
            }
        }
    },

    created() {
        this.editorListeners = omit(this.$listeners, ['selected-items', 'previous', 'next', 'select-page', 'side-panel-closed']);
    },

    mounted() {
        this.calculateMaxRows();

        if (this.resizable) {
            this.$nextTick(() => {
                this.resizableInstance = ResizableTable({
                    id: this.id,
                    columnIds: this.columns.map(column => column.field),
                    tableEl: this.$refs.innerTable,
                    wrapperEl: this.$refs.tableWrapper,
                    resizeCursor: RESIZABLE_CURSOR,
                    handleWidth: RESIZABLE_HANDLE_WIDTH,
                    minColumnWidth: RESIZABLE_MIN_COLUMN_WIDTH,
                    onColumnsUpdate: (columnWidths) => {
                        this.columnWidths = columnWidths;
                    },
                    onCursorChange: (hasResizeCursor, colId) => {
                        this.hasResizeCursor = hasResizeCursor;
                        this.columnBeingResized = (hasResizeCursor && colId) || null;
                    },
                    onColumnResize: (colId, width) => {
                        this.$set(this.columnWidths, colId, width);
                    },
                });
            });
        }
    },

    beforeDestroy() {
        if (this.resizableInstance) {
            this.resizableInstance.unmount();
        }
    },

    methods: {
        /**
         * Go to the previous page
         */
        previous() {
            this.currentPage--;
            this.$emit('previous', this.currentPage);
        },

        /**
         * Go to the next page
         */
        next() {
            this.currentPage++;
            this.$emit('next', this.currentPage);
        },

        /**
         * Go to the selected page
         *
         * @param {Number} pageNumber the page to select
         */
        selectPage(pageNumber) {
            this.currentPage = pageNumber;
            this.$emit('select-page', this.currentPage);
        },

        /**
         * Set the content for the side panel
         *
         * @param {Object} content
         */
        setPanelContent(content) {
            if (!this.editor) {
                console.warn('[SmartTable] Editor not configured');
                return;
            }
            this.sidePanelContent = content;
        },

        /**
         * Closes the side panel
         */
        closeSidePanel() {
            this.sidePanelContent = null;
            this.$emit('side-panel-closed');
        },

        /**
         * Close the side panel and reset the page to 1.
         */
        resetDisplay() {
            this.closeSidePanel();
            // here we don't want to use selectPage because we don't want to emit.
            this.currentPage = 1;
        },

        /**
         * Checks if the given item is being edited
         *
         * @param {object} item
         *
         * @returns {boolean}
         */
        isBeingEdited(item) {
            return this.sidePanelContent && this.sidePanelContent[this.trackBy] === item[this.trackBy];
        },

        /**
         * Checks if the given item is selected
         *
         * @param {object} item
         *
         * @returns {boolean}
         */
        isSelected(item) {
            let selected = this.selectedItemsIds.includes(item[this.trackBy]) || this.isBeingEdited(item);

            if (typeof this.itemSelectedExternalCheck === 'function') {
                selected = selected || this.itemSelectedExternalCheck(item);
            }

            return selected;
        },

        /**
         * Checks if the given item verses in a indeterminate state
         *
         * @param {object} item
         *
         * @returns {boolean}
         */
        isIndeterminate(item) {
            return !this.isSelected(item) && this.isBeingEdited(item) || this.indeterminates.includes(item[this.trackBy]);
        },

        calculateMaxRows() {
            if (!this.autoRows) {
                return;
            }
            this.incrementRows();
        },

        incrementRows() {
            this.$nextTick(() => {
                if (this.computedItemsPerPage * this.currentPage >= this.totalItems) {
                    return false;
                }

                const scrollParent = this.$utils.dom.getScrollParent(this.$refs.table);
                if (!this.$utils.dom.isElementOverflowing(scrollParent)) {
                    this.computedItemsPerPage++;
                    return this.incrementRows();
                } else {
                    this.computedItemsPerPage--;
                    return false;
                }
            });
        },

        getTableRowClasses(item) {
            return {
                selected: this.isSelected(item),
                [`${this.rowClass}__${item[this.rowClass]}`]: this.rowClass,
            };
        },
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"table",staticClass:"smart-table",class:_vm.computedClass},[(_vm.$scopedSlots['filters'] || _vm.$scopedSlots['actions'] || _vm.$scopedSlots['bulk-actions'])?_c('div',{staticClass:"list-buttons"},[_c('div',{staticClass:"filter-wrapper"},[_vm._t("filters")],2),(_vm.$scopedSlots['custom-columns'])?_c('div',{staticClass:"custom-columns-wrapper"},[_vm._t("custom-columns")],2):_vm._e(),(_vm.hasActions && !_vm.selectedItems.length)?_c('div',{staticClass:"calls-to-action"},[_vm._t("actions")],2):_vm._e(),(_vm.$scopedSlots['bulk-actions'] && _vm.selectedItems.length)?_c('div',{staticClass:"bulk-actions"},[_vm._t("bulk-actions",null,{"selectedItems":_vm.selectedItems})],2):_vm._e()]):_vm._e(),(_vm.items.length || _vm.showEmptyTable)?_c('div',{staticClass:"item-list",class:{'quick-editor-open': !!_vm.sidePanelContent, animating: _vm.animating}},[_c('div',{ref:"tableWrapper",staticClass:"table-wrapper"},[_c('div',{staticClass:"table-wrapper-content"},[_c('table',{ref:"innerTable",staticClass:"table-striped",class:{ actionable: _vm.selectable }},[_c('thead',[_c('tr',[(_vm.selectable)?_c('th',{staticClass:"selector"},[_c('input',{ref:"bulkToggler",attrs:{"type":"checkbox"},domProps:{"checked":_vm.allSelected},on:{"change":_vm.toggleAll}})]):(_vm.singleSelectable)?_c('th',{ref:"bulkToggler",staticClass:"selector"}):_vm._e(),_vm._t("headers",null,{"columnWidths":_vm.columnWidths,"columnClasses":_vm.columnClasses})],2)]),(_vm.items.length)?_c('tbody',_vm._l((_vm.visibleItems),function(item){return _c('tr',{key:item[_vm.trackBy],class:_vm.getTableRowClasses(item),on:{"click":function($event){return _vm.toggleContent(item)}}},[(_vm.selectable && !_vm.isItemSelectable(item) && _vm.isSelected(item))?_c('td',{staticClass:"selector"},[_c('input',{attrs:{"type":"checkbox","disabled":true},domProps:{"checked":true}})]):(_vm.selectable)?_c('td',{staticClass:"selector"},[_c('input',{directives:[{name:"model",rawName:"v-model",value:(_vm.selectedItemsIds),expression:"selectedItemsIds"}],attrs:{"type":"checkbox","disabled":!_vm.isItemSelectable(item)},domProps:{"value":item[_vm.trackBy],"indeterminate":_vm.isIndeterminate(item),"checked":Array.isArray(_vm.selectedItemsIds)?_vm._i(_vm.selectedItemsIds,item[_vm.trackBy])>-1:(_vm.selectedItemsIds)},on:{"change":function($event){var $$a=_vm.selectedItemsIds,$$el=$event.target,$$c=$$el.checked?(true):(false);if(Array.isArray($$a)){var $$v=item[_vm.trackBy],$$i=_vm._i($$a,$$v);if($$el.checked){$$i<0&&(_vm.selectedItemsIds=$$a.concat([$$v]))}else{$$i>-1&&(_vm.selectedItemsIds=$$a.slice(0,$$i).concat($$a.slice($$i+1)))}}else{_vm.selectedItemsIds=$$c}}}})]):_vm._e(),(_vm.singleSelectable && !_vm.selectable)?_c('td',{staticClass:"selector"},[_c('input',{attrs:{"type":"radio"},domProps:{"value":item[_vm.trackBy],"checked":_vm.isSelected(item)},on:{"change":function($event){_vm.selectedItemsIds = [item[_vm.trackBy]]}}})]):_vm._e(),_vm._t("row",null,{"item":item,"indeterminates":_vm.indeterminates,"setPanelContent":_vm.setPanelContent,"sidePanelContent":_vm.sidePanelContent,"columnClasses":_vm.columnClasses})],2)}),0):_vm._e()])])]),_c('transition',{attrs:{"name":"open"},on:{"before-enter":function($event){_vm.animating = true},"after-enter":function($event){_vm.animating = false},"before-leave":function($event){_vm.animating = true},"after-leave":function($event){_vm.animating = false}}},[(_vm.editor && _vm.sidePanelContent)?_c('list-quick-editor',_vm._g(_vm._b({attrs:{"component":_vm.editor,"content":_vm.sidePanelContent,"event":_vm.event,"track-by":_vm.trackBy},on:{"animating":function (status) { return _vm.animating = status; },"close":_vm.closeSidePanel}},'list-quick-editor',_vm.editorAttrs,false),_vm.editorListeners)):_vm._e()],1)],1):_vm._e(),(_vm.items.length && !_vm.hidePagination)?_c('paginator',{attrs:{"show-page-numbers":_vm.showPageNumbers,"max":_vm.maxPage,"current":_vm.currentPage},on:{"previous":_vm.previous,"next":_vm.next,"select-page":_vm.selectPage}}):_vm._e(),(!_vm.items.length && !_vm.showEmptyTable)?_c('div',{staticClass:"empty-items"},[_vm._t("empty-items")],2):_vm._e()],1)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "SmartTable.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__